import React, { useState } from 'react'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography, CircularProgress } from '@mui/material'
import { Email, Send } from '@mui/icons-material'
import { useEmailTestService } from '../../../use-cases/email-test'
import { validateEmail } from '../../../veles-validations'

interface EmailTestButtonProps {
    isBackoffice?: boolean
    disabled?: boolean
    variant?: 'contained' | 'outlined' | 'text'
    size?: 'small' | 'medium' | 'large'
    fullWidth?: boolean
}

export const EmailTestButton: React.FC<EmailTestButtonProps> = ({
    isBackoffice = false,
    disabled = false,
    variant = 'outlined',
    size = 'medium',
    fullWidth = false
}) => {
    const [open, setOpen] = useState(false)
    const [testEmail, setTestEmail] = useState('')
    const [emailError, setEmailError] = useState('')
    const { sendTestEmail, isLoading } = useEmailTestService()

    const handleOpen = () => {
        setOpen(true)
        setTestEmail('')
        setEmailError('')
    }

    const handleClose = () => {
        setOpen(false)
        setTestEmail('')
        setEmailError('')
    }

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setTestEmail(value)
        
        if (value) {
            const errorMessage = validateEmail(value)
            if (errorMessage) {
                setEmailError(errorMessage)
            } else {
                setEmailError('')
            }
        } else {
            setEmailError('')
        }
    }

    const handleSendTest = async () => {
        if (!testEmail || emailError) {
            setEmailError('Digite um email válido')
            return
        }

        try {
            await sendTestEmail(testEmail, isBackoffice)
            handleClose()
        } catch (error) {
            // Error já é tratado no service
        }
    }

    return (
        <>
            <Button
                variant={variant}
                color="primary"
                startIcon={<Email />}
                onClick={handleOpen}
                disabled={disabled}
                size={size}
                fullWidth={fullWidth}
                sx={{
                    textTransform: 'none',
                    fontWeight: 500
                }}
            >
                Testar Email
            </Button>

            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Email color="primary" />
                        <Typography variant="h6">
                            Enviar Email de Teste
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <DialogContent>
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Digite o email para onde deseja enviar o teste de configuração:
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label="Email de Teste"
                            type="email"
                            value={testEmail}
                            onChange={handleEmailChange}
                            error={!!emailError}
                            helperText={emailError || 'Digite um email válido para receber o teste'}
                            placeholder="exemplo@email.com"
                            disabled={isLoading}
                        />
                    </Box>
                </DialogContent>
                
                <DialogActions>
                    <Button 
                        onClick={handleClose}
                        disabled={isLoading}
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSendTest}
                        disabled={isLoading || !testEmail || !!emailError}
                        variant="contained"
                        startIcon={isLoading ? <CircularProgress size={16} /> : <Send />}
                        color="primary"
                    >
                        {isLoading ? 'Enviando...' : 'Enviar Teste'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
